import { usePolicy } from "@ice/react";
import {
    type ReactElement,
    type ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useTranslation } from "react-i18next";
import { POLICY_NAMES, type PolicyName } from "../commons/policies";
import { useErrorNotification } from "../hooks/useErrorNotification";
import { useWorkspaceId } from "../hooks/useWorkspaceId";
import { PermissionsContext } from "./PermissionsContext";

// Derived from usePolicy itself: the resource type lives in @ice/core, which is
// not a direct dependency of the app.
type PolicyResources = NonNullable<
    Parameters<typeof usePolicy>[0]["resources"]
>;

/**
 * Feature-level policies depend on user + workspace only: the workspace travels
 * as the resource's tenancy, while the resource `id` the authorizer requires is
 * a fixed placeholder (it is also the key of its answer, see PolicyEvaluator).
 */
const FEATURE_RESOURCE_ID = "fulfilments";

/** Resource shape for feature-level policies: the only place to change it. */
const buildWorkspaceResources = (workspaceId: string): PolicyResources => [
    {
        id: FEATURE_RESOURCE_ID,
        tenancy: { id: workspaceId, type: "WORKSPACE" },
    },
];

interface PolicyEvaluatorProps {
    policyName: PolicyName;
    resources: PolicyResources;
    onResult: (
        policyName: PolicyName,
        allowed: boolean,
        isError: boolean,
    ) => void;
}

/**
 * The only place in the app that calls usePolicy. Since 2.0.0-rc.2 the hook is
 * atom-based with an unambiguous contract: it starts with isLoading=true and
 * `results` stays undefined until the evaluation is settled (and when the atom
 * is already resolved by an earlier mount, the very first render is already
 * settled). So every render with isLoading=false ALWAYS carries a result; an
 * error, a missing/empty `results` or a missing resource means false
 * (fail-closed). No sentinel value, no latch.
 *
 * The SDK settles on its own in every case: it times the request out at 5s and
 * retries once, then fails — which arrives here as isError.
 */
const PolicyEvaluator = ({
    policyName,
    resources,
    onResult,
}: PolicyEvaluatorProps): null => {
    const { isLoading, isError, results } = usePolicy({
        action: policyName,
        resources,
    });

    useEffect(() => {
        if (isLoading) return;
        const allowed =
            !isError &&
            // `every` on an empty array is true: without this guard an evaluator
            // mounted with no resource would fail OPEN.
            resources.length > 0 &&
            resources.every(
                (resource) => results?.[resource.id]?.result === true,
            );
        onResult(policyName, allowed, isError);
    }, [isLoading, isError, results, resources, policyName, onResult]);

    return null;
};

interface PermissionsProviderProps {
    children: ReactNode;
}

export const PermissionsProvider = ({
    children,
}: PermissionsProviderProps): ReactElement => {
    const workspaceId = useWorkspaceId();
    const { t } = useTranslation();
    const { showTextMessage } = useErrorNotification();

    const [permissions, setPermissions] = useState<
        Partial<Record<PolicyName, boolean>>
    >({});
    const [hasFailed, setHasFailed] = useState(false);

    // Synchronous reset when the workspace changes (no frame with stale permissions).
    const [lastWorkspaceId, setLastWorkspaceId] = useState(workspaceId);
    if (lastWorkspaceId !== workspaceId) {
        setLastWorkspaceId(workspaceId);
        setPermissions({});
        setHasFailed(false);
    }

    const resources = useMemo(
        () => (workspaceId ? buildWorkspaceResources(workspaceId) : []),
        [workspaceId],
    );

    const handleResult = useCallback(
        (policyName: PolicyName, allowed: boolean, isError: boolean) => {
            if (isError) setHasFailed(true);
            setPermissions((prev) =>
                prev[policyName] === allowed
                    ? prev
                    : { ...prev, [policyName]: allowed },
            );
        },
        [],
    );

    // An unresolved workspace means loading, not "no policy to evaluate":
    // otherwise the gate would open with everything denied for one commit and
    // the children would be mounted, unmounted and remounted as soon as the
    // policies start (mount effects running twice).
    const isLoading =
        workspaceId === undefined ||
        POLICY_NAMES.some((name) => permissions[name] === undefined);

    // One toast per mount, however many policies failed: without it the actions
    // would simply be missing, indistinguishable from a user who lacks the
    // permission. The ref — not the dependency list — is what makes it once:
    // `t` and `showTextMessage` change identity on a re-render.
    const hasNotifiedRef = useRef(false);
    useEffect(() => {
        if (!hasFailed || hasNotifiedRef.current) return;
        hasNotifiedRef.current = true;
        showTextMessage(t("errors.messages.evaluatePermissions"));
    }, [hasFailed, showTextMessage, t]);

    const value = useMemo(
        () => ({ permissions, isLoading }),
        [permissions, isLoading],
    );

    return (
        <PermissionsContext.Provider value={value}>
            {workspaceId !== undefined &&
                POLICY_NAMES.map((policyName) => (
                    <PolicyEvaluator
                        key={policyName}
                        policyName={policyName}
                        resources={resources}
                        onResult={handleResult}
                    />
                ))}
            {children}
        </PermissionsContext.Provider>
    );
};
